import { isCanlan } from '@sportninja/common/utils/customer-name'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import type { RegistrationAdminData } from '@sportninja/common/services/registration/getRegistrationAdmin'
import type { ProcessingFeeBearer } from './processingFees'

/**
 * The slice of RegistrationSetupState derived from the /admin payload.
 * Everything else in that state is UI-local (errors, loading, dirty flags) and
 * must not be touched by hydration.
 */
export type HydratedRegistrationState = {
  competitionDateRange: { starts_at: string | null; ends_at: string | null }
  currency_country_id: string
  payment_provider: string | null
  taxes: string[]
  bccEmails: string[]
  registrationType: 'player' | 'team'
  paidBy: 'player' | 'team'
  darkMode: boolean
  customBackgroundImage: string
  customBackgroundImageChanged: boolean
  enableTeamRegistration: boolean
  enablePlayerRegistration: boolean
  autoApproveRegistrations: boolean
  show_waiver: boolean
  waivers: string[]
  addPlayersOutsideOfRegistration: boolean
  player_form: any
  team_form: any
  headerContent: string
  teamConfirmationEmailContent: string
  playerConfirmationEmailContent: string
  paymentProcessingFeeBearer: ProcessingFeeBearer
  paymentProcessingFeeVisible: boolean
  leagueProcessingFeeEnabled: boolean
  leagueProcessingFeePercent: number | null
  leagueProcessingFeeFlat: number | null
  leagueProcessingFeeVisible: boolean
  refundPaymentProcessingFee: boolean
}

/**
 * Map the /admin payload onto the setup form's state.
 *
 * Pure by design: the previous version of this logic lived inline in a
 * useEffect and could not be tested. The mappings here are load-bearing —
 * several fall back to empty/default values, so a payload key going missing
 * would silently blank a saved setting rather than error.
 */
export const hydrateFromAdmin = (
  data: RegistrationAdminData
): HydratedRegistrationState => ({
  competitionDateRange: {
    starts_at: data.starts_at || '',
    ends_at: data.ends_at || '',
  },
  currency_country_id: String(data.currency_country?.id || ''),
  payment_provider: data.payment_provider_type?.id ?? null,
  taxes: data.taxes?.map((tax) => tax.id) || [],
  bccEmails: data.bcc_registrations_to
    ? data.bcc_registrations_to.split(',')
    : [],
  registrationType: data.registration_type_id === 1 ? 'team' : 'player',
  paidBy: 'team',
  darkMode: Boolean(data.dark_theme),
  customBackgroundImage: resolveAssetUrl(data.image as never) || '',
  customBackgroundImageChanged: false,
  enableTeamRegistration: Boolean(data.is_team_registration),
  enablePlayerRegistration: Boolean(data.is_player_registration),
  // Canlan never auto-approves, regardless of what the API returns.
  autoApproveRegistrations: isCanlan ? false : Boolean(data.auto_approve),
  show_waiver: Boolean(data.show_waiver),
  waivers: data.waivers?.map((waiver) => waiver.id) || [],
  addPlayersOutsideOfRegistration: Boolean(data.can_add_players_from_roster),
  player_form: data.player_form ?? null,
  team_form: data.team_form ?? null,
  headerContent: data.header_content || '',
  teamConfirmationEmailContent: data.team_confirmation_email_content || '',
  playerConfirmationEmailContent: data.player_confirmation_email_content || '',
  paymentProcessingFeeBearer:
    data.payment_processing_fee_bearer === 'registrant'
      ? 'registrant'
      : 'league',
  paymentProcessingFeeVisible: data.payment_processing_fee_visible ?? true,
  // "Both blank or zero" means the master switch is off.
  leagueProcessingFeeEnabled:
    Number(data.league_processing_fee_percent ?? 0) > 0 ||
    Number(data.league_processing_fee_flat ?? 0) > 0,
  leagueProcessingFeePercent: data.league_processing_fee_percent ?? null,
  leagueProcessingFeeFlat: data.league_processing_fee_flat ?? null,
  leagueProcessingFeeVisible: data.league_processing_fee_visible ?? true,
  // Defaults off: unless the league opts in, the registrant bears the
  // processing fee on a refund — matching the pre-existing behaviour.
  refundPaymentProcessingFee: Boolean(data.refund_payment_processing_fee),
})

/**
 * Whether to show the skeleton: only on the cold initial fetch, never on a
 * background refetch that already has data. Gating on `loading` alone would
 * blank the filled form back to a skeleton after every save, since the save
 * handler triggers a refetch.
 */
export const isInitialAdminLoad = (
  loading: boolean,
  data: RegistrationAdminData | null
): boolean => loading && !data
